import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";

// --- Mock Data: Sample of target accounts for the plan ---
const SAMPLE_LEADS = [
  {
    date: "Plan",
    company: "Adobe",
    region: "NAMER",
    title: "Chief of Staff - Eng",
    asset: "AI Readiness Report",
    status: "Target",
  },
  {
    date: "Plan",
    company: "Capital One",
    region: "NAMER",
    title: "Sr Dir, Enterprise Data",
    asset: "GenAI Skills Brief",
    status: "Target",
  },
  {
    date: "Plan",
    company: "T Rowe Price",
    region: "NAMER",
    title: "VP HR Digital Exp",
    asset: "AI in HR Playbook",
    status: "Target",
  },
  {
    date: "Plan",
    company: "Google",
    region: "NAMER",
    title: "VP Connected Data",
    asset: "Data Mesh Webinar",
    status: "Target",
  },
  {
    date: "Plan",
    company: "Infosys",
    region: "India",
    title: "VP Learning & Dev",
    asset: "GenAI Skills Brief",
    status: "Target",
  },
  {
    date: "Plan",
    company: "Tata Consultancy",
    region: "India",
    title: "Head of AI Labs",
    asset: "AI Readiness Report",
    status: "Target",
  },
  {
    date: "Plan",
    company: "Grab",
    region: "SEA",
    title: "Head of Engineering",
    asset: "Modern Data Gov",
    status: "Target",
  },
  {
    date: "Plan",
    company: "GoTo Group",
    region: "SEA",
    title: "VP Data Science",
    asset: "AI Readiness Report",
    status: "Target",
  },
];

const QUARTER_GOALS = {
  "Q1 2026": { NAMER: 1000, India: 800, SEA: 400 },
  "Q2 2026": { NAMER: 1200, India: 900, SEA: 500 },
  "Q3 2026": { NAMER: 1500, India: 1000, SEA: 600 },
  "Q4 2026": { NAMER: 1500, India: 1200, SEA: 600 },
};

const COLORS = {
  NAMER: "#3b82f6", // Blue
  India: "#f59e0b", // Orange
  SEA: "#22c55e", // Green
};

const ContentSyndication = () => {
  const [quarter, setQuarter] = useState("Q1 2026");
  const goals = QUARTER_GOALS[quarter];

  const totalGuarantee = goals.NAMER + goals.India + goals.SEA;

  // Prepare Data for Visuals
  const data = useMemo(
    () => [
      {
        name: "NAMER",
        value: goals.NAMER,
        color: COLORS.NAMER,
        pct: Math.round((goals.NAMER / totalGuarantee) * 100),
      },
      {
        name: "India",
        value: goals.India,
        color: COLORS.India,
        pct: Math.round((goals.India / totalGuarantee) * 100),
      },
      {
        name: "SEA",
        value: goals.SEA,
        color: COLORS.SEA,
        pct: Math.round((goals.SEA / totalGuarantee) * 100),
      },
    ],
    [goals, totalGuarantee]
  );

  return (
    <div className="cs-wrapper">
      {/* --- Controls & KPI Strip --- */}
      <div className="cs-controls">
        <div className="cs-control-group">
          <label className="cs-label">Quarter Select</label>
          <div className="tabs" style={{ margin: 0, padding: 0 }}>
            {Object.keys(QUARTER_GOALS).map((q) => (
              <button
                key={q}
                className={`tab ${quarter === q ? "active" : ""}`}
                onClick={() => setQuarter(q)}
                style={{ fontSize: "13px", padding: "6px 12px" }}
              >
                {q}
              </button>
            ))}
          </div>
        </div>

        {/* Breakdown Section */}
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          {/* Regional Context Numbers */}
          <div
            style={{
              display: "flex",
              gap: "20px",
              paddingRight: "24px",
              borderRight: "1px solid #e2e8f0",
            }}
          >
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: "11px",
                  color: COLORS.NAMER,
                  fontWeight: 800,
                }}
              >
                NAMER
              </div>
              <div style={{ fontSize: "18px", fontWeight: 600 }}>
                {goals.NAMER.toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: "11px",
                  color: COLORS.India,
                  fontWeight: 800,
                }}
              >
                India
              </div>
              <div style={{ fontSize: "18px", fontWeight: 600 }}>
                {goals.India.toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{ fontSize: "11px", color: COLORS.SEA, fontWeight: 800 }}
              >
                SEA
              </div>
              <div style={{ fontSize: "18px", fontWeight: 600 }}>
                {goals.SEA.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Total */}
          <div style={{ textAlign: "right" }}>
            <div className="cs-label">Total Guarantee</div>
            <div
              style={{
                fontSize: "24px",
                fontWeight: 800,
                color: "#0f172a",
                lineHeight: "1",
              }}
            >
              {totalGuarantee.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* --- Visual Dashboard --- */}
      <div className="cs-grid-viz">
        {/* Left: Volume Chart */}
        <div className="card">
          <div className="card-header">
            <h3>Regional Volume</h3>
            <div className="sub">Lead Quantity by Region</div>
          </div>
          <div style={{ height: "280px", marginTop: "10px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                barCategoryGap="20%"
                margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fontWeight: 600 }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  cursor={{ fill: "#f8fafc" }}
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                  formatter={(val) => [val, "Guaranteed Leads"]}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} name="Leads">
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right: Mix/Distribution Chart */}
        <div className="card">
          <div className="card-header">
            <h3>Geographic Mix</h3>
            <div className="sub">% of Total Leads</div>
          </div>
          <div
            style={{ height: "280px", marginTop: "10px", position: "relative" }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      stroke="none"
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(val, name, props) => [
                    `${props.payload.pct}%`,
                    name,
                  ]}
                />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                  iconType="circle"
                  iconSize={8}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Center Text Overlay */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-85%, -50%)", // Visual center adjustment
                textAlign: "center",
                pointerEvents: "none",
              }}
            >
              <div style={{ fontSize: "12px", color: "#64748b" }}>Total</div>
              <div style={{ fontSize: "18px", fontWeight: 800 }}>
                {(totalGuarantee / 1000).toFixed(1)}k
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Sample List --- */}
      <div className="card">
        <div className="card-header">
          <h3>Target Account Mapping</h3>
          <div className="sub">
            Sample alignment of content assets to target regions/accounts.
          </div>
        </div>

        <div
          className="table-wrap"
          style={{ border: "none", boxShadow: "none" }}
        >
          <table className="table">
            <thead>
              <tr>
                <th>Phase</th>
                <th>Region</th>
                <th>Target Account</th>
                <th>Job Title Alignment</th>
                <th>Syndicated Asset</th>
                <th>List Status</th>
              </tr>
            </thead>
            <tbody>
              {SAMPLE_LEADS.map((lead, i) => (
                <tr key={i}>
                  <td style={{ color: "#64748b", fontSize: "12px" }}>
                    {lead.date}
                  </td>
                  <td style={{ fontWeight: 600, fontSize: "12px" }}>
                    {lead.region}
                  </td>
                  <td style={{ fontWeight: 600 }}>{lead.company}</td>
                  <td>{lead.title}</td>
                  <td style={{ color: "#3b82f6" }}>{lead.asset}</td>
                  <td>
                    <span
                      className="badge neutral"
                      style={{ fontSize: "11px" }}
                    >
                      {lead.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              padding: "12px",
              textAlign: "center",
              color: "#64748b",
              fontSize: "12px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <span style={{ textDecoration: "underline", cursor: "pointer" }}>
              Upload Target Account List (CSV)
            </span>{" "}
            to refine mapping.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentSyndication;
